// =============================================================================
// SimSense Platform — Desktop Agent Status (Tauri Only)
// =============================================================================

import { useDesktopStore } from "../state/desktopStore";
import { remoteLog } from "../utils/logger";
import { useToast } from "../components/Toast";

interface SelectedFile {
  path: string;
  name: string;
}

export function DesktopStatus() {
  const {
    isTauri,
    isWatching,
    statusMessage,
    setStatusMessage,
    uploadProgress,
    setUploadProgress,
    startWatching,
    stopWatching,
    scanAndUpload,
    telemetryEnabled,
    enableTelemetry
  } = useDesktopStore();
  
  const toast = useToast();

  if (!isTauri) return null;

  const handleFixTelemetry = async () => {
    try {
      await enableTelemetry();
      toast.success("iRacing telemetry enabled! You may need to restart iRacing for changes to take effect.");
    } catch (err) {
      toast.error("Failed to update iRacing config. Please enable 'irsdkLog' manually in app.ini.");
    }
  };

  const handleManualImport = async () => {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const selected: SelectedFile = await invoke("pick_ibt_file");

      setStatusMessage("Uploading...");
      setUploadProgress(0);

      // Read file via plugin-fs and upload via WebView fetch (handles HttpOnly cookies)
      const { readFile } = await import("@tauri-apps/plugin-fs");
      const bytes = await readFile(selected.path);
      const blob = new Blob([bytes], { type: "application/octet-stream" });
      const file = new File([blob], selected.name);
      const formData = new FormData();
      formData.append("file", file);

      let resp = await fetch("/api/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      // Auto-refresh on 401
      if (resp.status === 401) {
        const refreshResp = await fetch("/api/ss/auth/refresh", {
          method: "POST",
          credentials: "include",
        });
        if (refreshResp.ok) {
          const retryForm = new FormData();
          retryForm.append("file", file);
          resp = await fetch("/api/upload", {
            method: "POST",
            credentials: "include",
            body: retryForm,
          });
        }
      }

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }));
        throw new Error(errorData.detail || "Upload failed");
      }

      setStatusMessage("Upload Complete");
    } catch (err: any) {
      if (err === "No file selected") return;
      console.error("Manual import failed", err);
      void remoteLog("Manual import failed", "error", { error: String(err), stack: err?.stack });
      setStatusMessage("Import Failed");
    } finally {
      setUploadProgress(null);
      setTimeout(() => {
        const { isWatching, uploadProgress } = useDesktopStore.getState();
        if (uploadProgress === null) {
          setStatusMessage(isWatching ? "Monitoring..." : "Agent Standby");
        }
      }, 3000);
    }
  };

  const toggleWatcher = () => {
    if (isWatching) {
      stopWatching();
    } else {
      startWatching();
    }
  };

  const isUploading = uploadProgress !== null;

  const tooltip = isWatching 
    ? "Desktop Agent is actively watching your iRacing telemetry folder for new files." 
    : "Desktop Agent is on standby. Click the status light to enable automatic uploads.";

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {telemetryEnabled === false && (
        <div 
          onClick={handleFixTelemetry}
          title="iRacing Telemetry is disabled in your app.ini. Click to fix."
          style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "6px", 
            padding: "4px 10px", 
            background: "rgba(234, 179, 8, 0.15)", 
            border: "1px solid rgba(234, 179, 8, 0.3)", 
            borderRadius: "4px",
            marginRight: "12px",
            cursor: "pointer",
            animation: "pulse 2s infinite"
          }}
        >
          <span style={{ fontSize: "14px" }}>⚠️</span>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#fbbf24", textTransform: "uppercase" }}>Fix iRacing Config</span>
        </div>
      )}
      
      <div 
        title={tooltip}
        style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "12px", 
          padding: "4px 12px", 
          background: "rgba(255,255,255,0.03)", 
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border)",
          marginRight: "12px",
          minWidth: "240px"
        }}>
        <div 
          onClick={toggleWatcher}
          title={isWatching ? "Click to stop monitoring" : "Click to start monitoring"}
          style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, cursor: "pointer" }}
        >
          <div style={{ 
            width: "8px", 
            height: "8px", 
            borderRadius: "50%", 
            background: isWatching ? "var(--good)" : "var(--warn)",
            boxShadow: isWatching ? "0 0 8px var(--good)" : "none",
            transition: "all 0.3s ease"
          }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: "10px", fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1 }}>
              {statusMessage}
            </span>
            {isUploading && (
              <div style={{ width: "100px", height: "2px", background: "rgba(255,255,255,0.1)", marginTop: "4px", borderRadius: "1px", overflow: "hidden" }}>
                <div style={{ width: `${uploadProgress}%`, height: "100%", background: "var(--accent)", transition: "width 0.2s ease" }} />
              </div>
            )}
          </div>
        </div>

        <button
          onClick={() => void scanAndUpload()}
          disabled={isUploading}
          title="Scan telemetry folder for missed files"
          style={{
            padding: "4px 8px",
            background: isUploading ? "transparent" : "var(--card)",
            color: "var(--text1)",
            border: "1px solid var(--border)",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 600,
            cursor: isUploading ? "wait" : "pointer",
            opacity: isUploading ? 0.7 : 1,
            transition: "all 0.2s ease"
          }}
        >
          Scan
        </button>
        <button
          onClick={handleManualImport}
          disabled={isUploading}
          title="Manually select and upload an .ibt file"
          style={{
            padding: "4px 8px",
            background: isUploading ? "transparent" : "var(--card)",
            color: "var(--text1)",
            border: "1px solid var(--border)",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: 600,
            cursor: isUploading ? "wait" : "pointer",
            opacity: isUploading ? 0.7 : 1,
            transition: "all 0.2s ease"
          }}
        >
          {isUploading ? `${uploadProgress}%` : "Import .ibt"}
        </button>
      </div>
    </div>
  );
}
